import { useMemo } from "react";
import { Box, Flex, Group, SimpleGrid, Text, useMantineTheme } from "@mantine/core";
import { IconCheck, IconX } from "@tabler/icons";
import Helper from "@/utils/helper";

const rules = [
    { label: "10+ characters", test: (value: string) => value.length >= 10 },
    { label: "Uppercase letter", test: (value: string) => /[A-Z]/.test(value) },
    { label: "Lowercase letter", test: (value: string) => /[a-z]/.test(value) },
    { label: "Number", test: (value: string) => /\d/.test(value) }
];

export const passwordRulesMet = (password: string) => rules.filter((rule) => rule.test(password)).length;

const PasswordStrengthGrid = ({ password }: { password: string }) => {
    const theme = useMantineTheme();
    const met = passwordRulesMet(password);
    const meterColors = [theme.colors.red[6], theme.colors.orange[6], theme.colors.yellow[6], theme.colors.green[6]];
    const meterColor = met === 0 ? theme.colors.gray[3] : meterColors[met - 1];

    return (
        <Box mt={8}>
            <SimpleGrid cols={4} spacing={4} aria-hidden="true">
                {rules.map((rule, index) => (
                    <Box key={rule.label} h={4} style={{ borderRadius: 2, background: index < met ? meterColor : theme.colors.gray[2] }} />
                ))}
            </SimpleGrid>
            <SimpleGrid cols={2} spacing={6} verticalSpacing={6} mt={10} component="ul" aria-label="Password requirements" style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {rules.map((rule) => {
                    const passed = rule.test(password);
                    return (
                        <Group key={rule.label} component="li" gap={8} wrap="nowrap">
                            <Box
                                w={16}
                                h={16}
                                style={{
                                    borderRadius: "50%",
                                    flexShrink: 0,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    background: passed ? theme.colors.green[6] : "transparent",
                                    border: `1.5px solid ${passed ? theme.colors.green[6] : theme.colors.gray[4]}`,
                                    color: "white"
                                }}
                            >
                                {passed && <IconCheck size={11} stroke={3} />}
                            </Box>
                            <Text size="xs" c={passed ? "dark" : "dimmed"}>{rule.label}</Text>
                        </Group>
                    );
                })}
            </SimpleGrid>
        </Box>
    );
};

const PasswordRequirements = ({ password, confirmPassword, layout = "list" }: { password: string; confirmPassword: string; layout?: "list" | "grid" }) => {
    const theme = useMantineTheme();

    const strengthError = useMemo(() => (
        Helper.getPasswordStrengthError(password, { skipBaseChecks: true })
    ), [password]);

    if (layout === "grid") return <PasswordStrengthGrid password={password} />;

    return (
        <>
            <Flex align="end" mt="sm" mb="xs" justify="center" px={5}>
                <Text size="sm" c="dimmed" mt="xs" mb="md">
                    Choose a strong password with at least 10 characters, including uppercase, lowercase, and a number.
                </Text>
            </Flex>

            <Flex gap={5} direction="column">
                <Group gap={5}>
                    {password.length < 10 ? (
                        <IconX size={18} color={theme.colors.yellow[7]} />
                    ) : (
                        <IconCheck size={18} color={theme.colors.scBlue[8]} />
                    )}
                    <Text size="sm" c="dimmed">At least 10 characters</Text>
                </Group>

                <Group gap={5}>
                    {!/[A-Z]/.test(password) ? (
                        <IconX size={18} color={theme.colors.yellow[7]} />
                    ) : (
                        <IconCheck size={18} color={theme.colors.scBlue[8]} />
                    )}
                    <Text size="sm" c="dimmed">Contains uppercase letter</Text>
                </Group>

                <Group gap={5}>
                    {!/[a-z]/.test(password) ? (
                        <IconX size={18} color={theme.colors.yellow[7]} />
                    ) : (
                        <IconCheck size={18} color={theme.colors.scBlue[8]} />
                    )}
                    <Text size="sm" c="dimmed">Contains lowercase letter</Text>
                </Group>

                <Group gap={5}>
                    {!/\d/.test(password) ? (
                        <IconX size={18} color={theme.colors.yellow[7]} />
                    ) : (
                        <IconCheck size={18} color={theme.colors.scBlue[8]} />
                    )}
                    <Text size="sm" c="dimmed">Contains number</Text>
                </Group>

                <Group gap={5}>
                    {/\s/.test(password) ? (
                        <IconX size={18} color={theme.colors.yellow[7]} />
                    ) : (
                        <IconCheck size={18} color={theme.colors.scBlue[8]} />
                    )}
                    <Text size="sm" c="dimmed">No spaces</Text>
                </Group>

                <Group gap={5}>
                    {strengthError != null ? (
                        <IconX size={18} color={theme.colors.yellow[7]} />
                    ) : (
                        <IconCheck size={18} color={theme.colors.scBlue[8]} />
                    )}
                    <Text size="sm" c="dimmed">
                        {strengthError || 'Avoids common patterns'}
                    </Text>
                </Group>

                <Group gap={5}>
                    {password === '' || password !== confirmPassword ? (
                        <IconX size={18} color={theme.colors.yellow[7]} />
                    ) : (
                        <IconCheck size={18} color={theme.colors.scBlue[8]} />
                    )}
                    <Text size="sm" c="dimmed">Passwords match</Text>
                </Group>
            </Flex>
        </>
    );
};

export default PasswordRequirements;
